import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, ChartLineUp, Lightning, Star, Robot, CheckCircle, ArrowRight, QrCode, MapPin,
  Storefront, Heart, Buildings, Briefcase,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/lib/useConfig";
import { Button } from "@/components/ui/button";
import ReviewsWall from "@/components/ReviewsWall";

const FEATURES = [
  { icon: MapPin, title: "One profile, everywhere", text: "Enter your business details once — name, address, phone, hours, photos — and reuse them across every listing." },
  { icon: MagnifyingGlass, title: "138+ directories", text: "Google, Yelp, Bing, Apple Maps, BBB, Foursquare, plus industry sites for medical, legal, real estate, food and more." },
  { icon: Robot, title: "AI platforms too", text: "Get surfaced by ChatGPT, Perplexity, Google AI Overviews, Copilot & Siri — the new way customers find businesses." },
  { icon: Lightning, title: "Bulk Assist", text: "Copy your info once and open each site pre-filled — submit in seconds instead of retyping everywhere." },
  { icon: ChartLineUp, title: "Live progress tracking", text: "See what's live, in progress or rejected. Paste a link and it auto-marks live with a timestamp." },
  { icon: Star, title: "Reviews & feedback", text: "Request honest feedback from every customer with a QR code or link, monitor responses, and make it easy for customers to share their experience." },
];

const PLATFORMS = [
  "Google", "Yelp", "Bing Places", "Apple Maps", "Facebook", "Foursquare",
  "BBB", "ChatGPT", "Perplexity", "Google AI Overviews", "Copilot", "Siri",
];

const TARGET_PERSONAS = [
  { icon: Storefront, title: "Local service businesses", text: "HVAC, plumbing, roofing, cleaning and contractors who need to be found across every map and directory." },
  { icon: Heart, title: "Healthcare & wellness", text: "Dentists, clinics, salons and studios keeping accurate NAP details on medical and review sites." },
  { icon: Buildings, title: "Multi-location & franchises", text: "Keep every location consistent across directories from one dashboard." },
  { icon: Briefcase, title: "Marketing agencies", text: "Manage citations for dozens of clients with progress tracking and CSV reporting." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const config = useConfig();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const dirCount = config?.directory_count || 138;

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center group">
            <img src="/citehub-logo.png" alt="Local CiteHub" width="120" height="50" className="h-9 w-auto transition-transform group-hover:-translate-y-0.5" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate("/login")} data-testid="nav-signin"
              className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-10">Sign in</Button>
            <Button onClick={() => navigate("/login?signup=1")} data-testid="nav-get-started"
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-10">Get started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#0055FF]">
          <Robot weight="fill" size={14} /> Now with AI platform listings
        </span>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.03] max-w-4xl mx-auto">
          Get your business found on every directory — and every AI.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          CiteHub puts your business info in one place and helps you publish, track and manage consistent listings
          across {dirCount}+ US directories — from Google and Yelp to ChatGPT, Perplexity and Google AI Overviews.
          Collect reviews, monitor progress, and keep every listing accurate.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Button onClick={() => navigate("/login?signup=1")} data-testid="hero-cta"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-12 px-7 text-base group">
            Get listed now <ArrowRight size={18} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <a href="#pricing"><Button variant="secondary" className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-12 px-7 text-base">See pricing</Button></a>
        </div>
        <p className="mt-4 text-sm text-slate-400">New here? <button onClick={() => navigate("/login?signup=1")} data-testid="hero-signup-link" className="font-semibold text-[#0055FF] hover:underline">Create an account</button> · Already a member? <button onClick={() => navigate("/login")} data-testid="hero-signin-link" className="font-semibold text-slate-600 hover:underline">Sign in</button></p>
      </section>

      {/* Trusted platforms strip */}
      <section className="border-y border-slate-200 bg-white" data-testid="platform-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Publish and track across</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
            {PLATFORMS.map((p) => (
              <span key={p} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm font-semibold text-slate-600">
                {p}
              </span>
            ))}
            <span className="rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-sm font-bold text-[#0055FF]">
              + {dirCount}+ more
            </span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl font-black tracking-tight text-slate-900">Everything you need to own local search</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="h-11 w-11 rounded-md bg-blue-50 flex items-center justify-center"><f.icon weight="duotone" size={24} className="text-[#0055FF]" /></div>
                <h3 className="font-display text-lg font-bold text-slate-900 mt-4">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews wall (falls back to "Built for" personas when there aren't enough reviews yet) */}
      <ReviewsWall fallback={<BuiltFor />} />

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF]">Pricing</p>
          <h2 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900">Simple plans. Cancel anytime.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6 items-start">
          {plans.map((plan) => {
            const recommended = plan.id === "self_serve";
            return (
              <div key={plan.id} data-testid={`landing-plan-${plan.id}`}
                className={`relative rounded-2xl border-2 p-8 ${recommended ? "border-[#0055FF] shadow-lg" : "border-slate-200"}`}>
                <span className={`absolute -top-3 left-8 rounded-full text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1 ${recommended ? "bg-[#0055FF]" : "bg-slate-900"}`}>
                  {recommended ? "Recommended" : "Done for you"}
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#0055FF]">{plan.name}</p>
                <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display text-5xl font-black tracking-tighter text-slate-900">${plan.monthly_amount}</span>
                  <span className="text-slate-500 mb-1.5 text-sm font-semibold">/month</span>
                </div>
                {plan.setup_amount > 0
                  ? <p className="mt-1.5 text-sm text-slate-600">First month <span className="font-bold text-slate-900">${plan.first_invoice}</span> (one-time setup), then ${plan.monthly_amount}/mo.</p>
                  : <p className="mt-1.5 text-sm text-slate-600">Flat <span className="font-bold text-slate-900">${plan.monthly_amount}/mo</span>. No setup fee.</p>}
                <div className="my-6 h-px bg-slate-100" />
                <ul className="space-y-2.5">
                  {plan.features.map((ft) => (
                    <li key={ft} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <CheckCircle weight="fill" size={18} className={`shrink-0 mt-0.5 ${recommended ? "text-emerald-500" : "text-[#0055FF]"}`} />
                      <span>{ft}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate("/login?signup=1")} data-testid={`landing-cta-${plan.id}`}
                  className={`mt-7 w-full h-12 font-semibold ${recommended ? "bg-[#0055FF] hover:bg-[#0044CC] text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  Get started
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">Have a promo code? Enter <span className="font-mono font-bold text-slate-700">3RDFREE</span> at checkout for your 3rd month free.</p>
      </section>

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-white">Ready to be everywhere your customers search?</h2>
          <p className="mt-3 text-slate-300">Start today — list across {dirCount}+ directories and the major AI platforms.</p>
          <Button onClick={() => navigate("/login?signup=1")} data-testid="footer-cta"
            className="mt-7 bg-[#0055FF] hover:bg-[#0044CC] text-white h-12 px-8 text-base">Get started</Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div>
              <Link to="/" data-testid="footer-logo-link" className="inline-flex items-center">
                <img src="/citehub-logo.png" alt="Local CiteHub" width="110" height="46" className="h-7 w-auto" />
              </Link>
              <p className="text-sm text-slate-500 mt-3 max-w-xs">Business listings & citation management across {dirCount}+ US directories and AI platforms.</p>
            </div>
            <nav className="flex flex-wrap gap-x-8 gap-y-3 text-sm" data-testid="footer-links">
              <Link to="/contact" data-testid="footer-contact" className="text-slate-600 hover:text-[#0055FF] font-medium">Contact</Link>
              <Link to="/support" data-testid="footer-support" className="text-slate-600 hover:text-[#0055FF] font-medium">Support</Link>
              <Link to="/privacy" data-testid="footer-privacy" className="text-slate-600 hover:text-[#0055FF] font-medium">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms" className="text-slate-600 hover:text-[#0055FF] font-medium">Terms of Service</Link>
              <Link to="/refund" data-testid="footer-refund" className="text-slate-600 hover:text-[#0055FF] font-medium">Cancellation & Refund</Link>
            </nav>
          </div>
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-slate-400">© {new Date().getFullYear()} Local CiteHub{config?.company_name ? ` · by ${config.company_name}` : ""}</p>
            <p className="text-xs text-slate-400">Made for local businesses and agencies.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const BuiltFor = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="built-for">
    <div className="text-center">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF]">Who it's for</p>
      <h2 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900">Built for businesses like yours</h2>
      <p className="mt-3 text-slate-600 max-w-2xl mx-auto">From single-location owners to agencies managing hundreds of listings — CiteHub keeps every citation accurate and discoverable.</p>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {TARGET_PERSONAS.map((p) => (
        <div key={p.title} data-testid={`persona-${p.title.split(" ")[0].toLowerCase()}`}
          className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="h-11 w-11 rounded-md bg-blue-50 flex items-center justify-center"><p.icon weight="duotone" size={24} className="text-[#0055FF]" /></div>
          <h3 className="font-display text-lg font-bold text-slate-900 mt-4">{p.title}</h3>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{p.text}</p>
        </div>
      ))}
    </div>
  </section>
);
